const validateEventName = value => {
  if (value == null || typeof value !== 'string') {
    throw new Error('[EventEmitter] eventName argument should be a type of string');
  }
};

const validateListenerFunction = value => {
  if (value == null || typeof value !== 'function') {
    throw new Error('[EventEmitter] listenerFunction argument should be a type of function');
  }
};

const validateMaxListenersCount = value => {
  if (value == null || typeof value !== 'number' || value < 1) {
    throw new Error('[EventEmitter] Max listeners count should be greater than 0');
  }
};

const checkListenersCount = (eventListeners, maxListenersCount) => {
  if (eventListeners.length >= maxListenersCount) {
    // eslint-disable-next-line no-console
    console.warn(
      `[EventEmitter] Possible memory leak detected. ${eventListeners.length} listeners added. Use emitter.setMaxListeners() to increase limit.`
    );
  }
};

export default class EventEmitter {
  constructor() {
    this.eventsListeners = new Map();
    this.maxListenersCount = 10;
  }

  on(eventName, listenerFunction) {
    validateEventName(eventName);
    validateListenerFunction(listenerFunction);

    let eventListeners = this.eventsListeners.get(eventName);

    if (eventListeners != null) {
      eventListeners.push(listenerFunction);
    } else {
      eventListeners = [listenerFunction];
      this.eventsListeners.set(eventName, eventListeners);
    }

    checkListenersCount(eventListeners, this.maxListenersCount);

    return this;
  }

  once(eventName, listenerFunction) {
    validateEventName(eventName);
    validateListenerFunction(listenerFunction);

    const wrappedListener = (...args) => {
      this.off(eventName, wrappedListener);
      return listenerFunction(...args);
    };

    this.on(eventName, wrappedListener);
    return this;
  }

  emit(eventName, ...args) {
    validateEventName(eventName);

    const eventListeners = this.eventsListeners.get(eventName);

    if (eventListeners == null) {
      return this;
    }

    eventListeners.forEach(listener => {
      listener(...args);
    });

    return this;
  }

  off(eventName, listenerFunction) {
    validateEventName(eventName);
    validateListenerFunction(listenerFunction);

    const eventListeners = this.eventsListeners.get(eventName);

    if (eventListeners == null) {
      return this;
    }

    const indexOfListener = eventListeners.indexOf(listenerFunction);

    if (indexOfListener === -1) {
      return this;
    }

    eventListeners.splice(indexOfListener, 1);
    return this;
  }

  eventNames() {
    return [...this.eventsListeners.keys()];
  }

  listenerCount(eventName) {
    validateEventName(eventName);
    const eventListeners = this.eventsListeners.get(eventName);
    return eventListeners ? eventListeners.length : 0;
  }

  listeners(eventName) {
    validateEventName(eventName);
    const eventListeners = this.eventsListeners.get(eventName);
    return eventListeners ? [...eventListeners] : [];
  }

  setMaxListeners(n) {
    validateMaxListenersCount(n);
    this.maxListenersCount = n;
    return this;
  }

  removeAllListeners(eventName) {
    if (eventName != null) {
      this.eventsListeners.delete(eventName);
      return this;
    }

    this.eventsListeners.clear();
    return this;
  }
}
